import { cn } from '@/lib/utils';
import { columnLabelClass } from '@/components/ui/typography.ts';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import PrimitiveInput from '@/components/form/_primitives/PrimitiveInput.tsx';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import { paymentMethodLabels } from '@/features/dashboard/tourConfig/types/PaymentMethod.utils.ts';
import { CorrectionTermsVm } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';

interface CorrectionTermsFieldsProps
  extends React.HTMLAttributes<HTMLDivElement> {
  terms: CorrectionTermsVm;
  dueDate: string | null;
  onDueDateChange: (dueDate: string | null) => void;
  paymentMethods: PaymentMethod[];
  onTogglePaymentMethod: (method: PaymentMethod) => void;
}

/**
 * Termin i metody płatności linii korekty — jedyne dwie wartości kroku W2,
 * których podgląd nie liczy, bo na żadną kwotę nie mają wpływu.
 *
 * `resolve` wymaga obu przy niezerowej kwocie paczki, więc pole musi tu być,
 * choćby operator go nie ruszył: podpowiedź bierze się z rat biorących udział
 * w rozkładzie (ostatni termin, część wspólna dopuszczonych metod).
 */
const CorrectionTermsFields = ({
  terms,
  dueDate,
  onDueDateChange,
  paymentMethods,
  onTogglePaymentMethod,
  className,
  ...props
}: CorrectionTermsFieldsProps) => (
  <div className={cn('mt-4', className)} {...props}>
    <div className={columnLabelClass}>Płatność korekty</div>

    <div className="mt-2.5 flex flex-wrap items-start gap-x-6 gap-y-3">
      <div className="flex flex-col gap-1">
        <InputLabel
          label="Termin płatności"
          required
          htmlFor="correction-due-date"
        />
        <PrimitiveInput
          id="correction-due-date"
          type="date"
          className="w-[168px]"
          value={dueDate ?? ''}
          onChange={(event) => onDueDateChange(event.target.value || null)}
        />
      </div>

      <div className="flex min-w-0 flex-col gap-1">
        <InputLabel label="Metody płatności" required />
        <div className="flex flex-wrap gap-2 pt-0.5">
          {terms.methodChoices.map((method) => {
            const selected = paymentMethods.includes(method);
            return (
              <button
                key={method}
                type="button"
                aria-pressed={selected}
                onClick={() => onTogglePaymentMethod(method)}
                className={cn(
                  'cursor-pointer rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors',
                  selected
                    ? 'border-accent-border bg-accent-200 text-accent-900'
                    : 'border-neutral-200 bg-basic-white text-neutral-600 hover:border-neutral-300',
                )}
              >
                {paymentMethodLabels[method]}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  </div>
);

export default CorrectionTermsFields;
